// ─── Sync domain constants, display configs, and helpers ────────────────────
//
// Shared between POS and Admin apps. Previously duplicated in both
// apps/pos/src/lib/sync-constants.ts and apps/admin/src/lib/sync-constants.ts.
//
// G2.18/G2.22: Two parallel "tone" type systems are in play:
//   - `Tone` (design-token Tone) — used by <GlassStatCard>. Values like
//     'brand', 'teal', 'violet' are valid (drive Tailwind classes via toneClasses()).
//   - `StatusKey` (Badge variant) — used by <Badge>. Only keys defined in
//     STATUS_COLORS are valid; 'brand'/'teal'/'violet' are NOT.
//
// `SYNC_STATUS_CONFIG` feeds GlassStatCard -> keeps Tone.
// `ENTITY_TYPE_CONFIG` / `RESOLUTION_CONFIG` feed Badge -> use StatusKey.

import type { Tone } from './design-tokens';
import type { StatusKey } from './status-colors';

// ─── Sync status display ────────────────────────────────────────────────────

export const SYNC_STATUS_CONFIG: Record<string, { label: string; tone: Tone; description: string }> = {
  idle:    { label: 'Synced',      tone: 'success', description: 'All data is up to date' },
  pushing: { label: 'Pushing...',  tone: 'brand',   description: 'Sending local changes to server' },
  pulling: { label: 'Pulling...',  tone: 'brand',   description: 'Downloading server updates' },
  error:   { label: 'Error',       tone: 'danger',  description: 'Sync failed — retry or check connection' },
};

// ─── Resolution display ─────────────────────────────────────────────────────

export const RESOLUTION_CONFIG: Record<string, { label: string; tone: StatusKey; description: string }> = {
  PENDING:      { label: 'Pending',      tone: 'pending',  description: 'Awaiting resolution' },
  CLIENT_WINS:  { label: 'Client Wins',  tone: 'success',  description: 'Local version accepted' },
  SERVER_WINS:  { label: 'Server Wins',  tone: 'info',     description: 'Server version accepted' },
  MERGED:       { label: 'Merged',       tone: 'approved', description: 'Both versions combined' },
  DISCARDED:    { label: 'Discarded',    tone: 'danger',   description: 'Change dropped' },
};

// ─── Entity type display ────────────────────────────────────────────────────

// G2.22: BILL_ITEM removed. Bills are pushed as atomic entities with items
// embedded in the BILL payload; the backend rejects BILL_ITEM at the DTO
// level (G2.20). There is no standalone bill-item sync row.
export const ENTITY_TYPE_CONFIG: Record<string, { label: string; tone: StatusKey }> = {
  BILL:             { label: 'Bill',             tone: 'confirmed'      },
  STOCK_ADJUSTMENT: { label: 'Stock Adjustment', tone: 'synced'         },
  PRODUCT:          { label: 'Product',          tone: 'tenantApproved' },
  EXPENSE:          { label: 'Expense',          tone: 'warning'        },
  CUSTOMER:         { label: 'Customer',         tone: 'info'           },
  QUOTATION:        { label: 'Quotation',        tone: 'quote'          },
};

// ─── Filter options ─────────────────────────────────────────────────────────

export const ENTITY_TYPE_OPTIONS = [
  { value: '', label: 'All Types' },
  { value: 'BILL', label: 'Bill' },
  { value: 'STOCK_ADJUSTMENT', label: 'Stock Adjustment' },
  { value: 'PRODUCT', label: 'Product' },
  { value: 'EXPENSE', label: 'Expense' },
  { value: 'CUSTOMER', label: 'Customer' },
  { value: 'QUOTATION', label: 'Quotation' },
];

export const RESOLUTION_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'CLIENT_WINS', label: 'Client Wins' },
  { value: 'SERVER_WINS', label: 'Server Wins' },
  { value: 'MERGED', label: 'Merged' },
  { value: 'DISCARDED', label: 'Discarded' },
];

// ─── Object diff helpers ────────────────────────────────────────────────────

/**
 * Flatten a nested object to dot-notation keys.
 * Arrays are flattened as `key[0]`, `key[1]`, etc.
 */
export function flattenObject(
  obj: Record<string, unknown>,
  prefix = '',
): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    const path = prefix ? `${prefix}.${key}` : key;
    if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
      Object.assign(result, flattenObject(value as Record<string, unknown>, path));
    } else if (Array.isArray(value)) {
      value.forEach((item, i) => {
        if (item !== null && typeof item === 'object') {
          Object.assign(result, flattenObject(item as Record<string, unknown>, `${path}[${i}]`));
        } else {
          result[`${path}[${i}]`] = item;
        }
      });
      if (value.length === 0) result[path] = '[]';
    } else {
      result[path] = value;
    }
  }
  return result;
}

/**
 * Diff two objects (flattened) and return categorized field keys.
 */
export function diffObjects(
  client: Record<string, unknown>,
  server: Record<string, unknown>,
): { changed: string[]; clientOnly: string[]; serverOnly: string[] } {
  const flatClient = flattenObject(client);
  const flatServer = flattenObject(server);

  const clientKeys = new Set(Object.keys(flatClient));
  const serverKeys = new Set(Object.keys(flatServer));

  const changed: string[] = [];
  const clientOnly: string[] = [];
  const serverOnly: string[] = [];

  for (const key of clientKeys) {
    if (!serverKeys.has(key)) {
      clientOnly.push(key);
    } else if (JSON.stringify(flatClient[key]) !== JSON.stringify(flatServer[key])) {
      changed.push(key);
    }
  }

  for (const key of serverKeys) {
    if (!clientKeys.has(key)) {
      serverOnly.push(key);
    }
  }

  return { changed, clientOnly, serverOnly };
}

// ─── Relative time helper ───────────────────────────────────────────────────

/** Format an ISO timestamp as a relative time string (e.g. "5 min ago"). */
export function relativeTimeSync(iso: string | null): string {
  if (!iso) return 'Never';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

// ─── Role check ─────────────────────────────────────────────────────────────

const MANAGER_PLUS = new Set(['MANAGER', 'OWNER', 'ADMIN', 'MODERATOR', 'SUPER_ADMIN']);
export function isManagerPlus(role: string): boolean {
  return MANAGER_PLUS.has(role);
}

/** Truncate a string and append ellipsis if exceeds max length. */
export function truncate(str: string, max: number): string {
  return str.length > max ? `${str.slice(0, max)}\u2026` : str;
}

/** Format a value for display in the diff table. */
export function formatDiffValue(value: unknown): string {
  if (value === null || value === undefined) return '\u2014';
  if (typeof value === 'string') return value;
  return JSON.stringify(value);
}
